import React, { useState, useRef, useEffect } from 'react';

interface VideoSource {
  src: string;
  media?: string;
  type?: string;
}

interface OptimizedVideoProps {
  sources: VideoSource[];
  poster?: string;
  posterMobile?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  className?: string;
  onCanPlay?: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
  onPlaying?: () => void;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  preload?: 'none' | 'metadata' | 'auto';
  eager?: boolean;
}

export default function OptimizedVideo({
  sources,
  poster,
  posterMobile,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  className = "",
  onCanPlay,
  onPlaying,
  onEnded,
  onProgress,
  preload = 'none',
  eager = false
}: OptimizedVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [activeSrc, setActiveSrc] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);

  // Intersection Observer to detect when video is near viewport
  useEffect(() => {
    if (eager) {
        setIsInView(true);
        return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [eager]);

  // Determine which video source to use based on media queries
  useEffect(() => {
    if (!isInView) return;

    const selectSource = () => {
      const matchedSource = sources.find(source => {
        if (!source.media) return true;
        return window.matchMedia(source.media).matches;
      }) || sources[0];

      if (matchedSource && matchedSource.src !== activeSrc) {
        setActiveSrc(matchedSource.src);
      }
    };

    selectSource();
    
    // Only re-check on major resize if needed
    const handleResize = () => {
        selectSource();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isInView, sources, activeSrc]);

  // Determine current poster URL
  useEffect(() => {
    const updatePoster = () => {
      if (posterMobile && window.matchMedia('(max-width: 800px)').matches) {
        setPosterUrl(posterMobile);
      } else {
        setPosterUrl(poster || null);
      }
    };

    updatePoster();
    window.addEventListener('resize', updatePoster);
    return () => window.removeEventListener('resize', updatePoster);
  }, [poster, posterMobile]);

  // Control playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeSrc) return;
    
    // Reset video state when source changes
    video.load();
    
    if (autoPlay) {
      // Use a small delay or wait for metadata to be safer, 
      // but browser autoPlay attribute often handles this better.
      // However, since we're setting src dynamically, we need to call play().
      const playVideo = async () => {
        try {
          await video.play();
        } catch (e: any) {
          // AbortError is common when source changes before play starts
          // NotSupportedError can happen if we play before sources are ready
          if (e.name !== 'AbortError' && e.name !== 'NotSupportedError') {
            console.warn("OptimizedVideo: Playback prevented:", e);
          }
          // If it's NotSupportedError, it might be a transient state during source switch.
          // We don't want to spam the console with a "Play error" prefix that looks like a crash.
        }
      };

      playVideo();
    } else {
      video.pause();
    }
  }, [autoPlay, activeSrc]);
  
  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* RESPONSIVE POSTER USING PICTURE FOR MAX PERFORMANCE */}
      {poster && (
        <picture className="absolute inset-0 w-full h-full">
          {posterMobile && <source media="(max-width: 800px)" srcSet={posterMobile} />}
          <source media="(min-width: 801px)" srcSet={poster} />
          <img 
            src={poster} 
            alt="" 
            className={`w-full h-full object-cover transition-opacity duration-700 ${isReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} 
            loading={eager ? "eager" : "lazy"}
            {...(eager ? { fetchPriority: "high" } as any : {})}
          />
        </picture>
      )}

      {activeSrc && (
        <video
          key={activeSrc}
          ref={videoRef}
          src={activeSrc}
          poster={posterUrl || undefined}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload={preload}
          className={`w-full h-full object-cover transition-opacity duration-700 ${isReady ? 'opacity-100' : 'opacity-0'}`}
          onCanPlay={onCanPlay}
          onPlaying={() => {
            setIsReady(true);
            if (onPlaying) onPlaying();
          }}
          onEnded={onEnded}
          onProgress={(e) => {
            if (onProgress && videoRef.current) {
              const video = videoRef.current;
              if (video.buffered.length > 0) {
                const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                const duration = video.duration;
                if (duration > 0) {
                  onProgress((bufferedEnd / duration) * 100);
                }
              }
            }
          }}
        />
      )}
    </div>
  );
}
